import * as React from 'react';
import { v4 as uuid } from 'uuid';
import { getLocalName, noop } from 'vet-bones/utils';
import { requestStardogVoiceboxGenesis } from 'vet-bones/utils/voicebox';

import { notificationsActionCreators } from 'src/actions/notifications/actionCreators';
import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import {
  EvaluationSummary,
  EvaluationSummaryIssueType,
} from 'src/constants/voicebox';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { Resource } from 'src/containers/ProjectDiagram/types/data';
import { isResource } from 'src/containers/ProjectDiagram/utils/data';
import { projectCompetencyQuestionsSelector } from 'src/selectors/projectCompetencyQuestions';
import { Project } from 'src/store/state/projects';
import { uiSlice } from 'src/store/ui/ui.slice';
import { useDefaultPortalConnection } from 'src/utils/hooks/useDefaultPortalConnection';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import { buildGenerateSummaryParameters } from 'src/utils/voicebox/hooks/parameters';
import {
  GenerateSummaryRequest,
  GenerateSummaryResponse,
} from 'src/utils/voicebox/hooks/types';
import { useEvaluateQuestions } from 'src/utils/voicebox/hooks/useEvaluateQuestions';

type IsIssuesOpenState = {
  unansweredQuestions: boolean;
  unusedResources: boolean;
  unlinkedConcepts: boolean;
  addQuestions: boolean;
  addDataset: boolean;
};

const defaultIsIssuesOpen: IsIssuesOpenState = {
  unansweredQuestions: false,
  unusedResources: false,
  unlinkedConcepts: false,
  addQuestions: false,
  addDataset: false,
};

export type EvaluateSummaryContextType = {
  evaluateSummary: (project?: Project) => Promise<EvaluationSummary | null>;
  isIssuesOpen: IsIssuesOpenState;
  isLoadingSummary: boolean;
  setIsIssuesOpen: (key: keyof IsIssuesOpenState, isOpen: boolean) => void;
};

export const EvaluateSummaryContext =
  React.createContext<EvaluateSummaryContextType>({
    evaluateSummary: async () => null,
    isIssuesOpen: { ...defaultIsIssuesOpen },
    isLoadingSummary: false,
    setIsIssuesOpen: noop,
  });

export const useEvaluateSummary = () => {
  return React.useContext(EvaluateSummaryContext);
};

export const useEvaluateSummaryContextValue =
  (): EvaluateSummaryContextType => {
    const { projectId } = useRouterParams();
    const diagram = useDiagram();
    const dispatch = useReduxDispatch();

    const defaultConnection = useDefaultPortalConnection();
    const questions = useReduxSelector(
      projectCompetencyQuestionsSelector(projectId)
    );

    const [isIssuesOpen, setIsIssuesOpenFn] = React.useState<IsIssuesOpenState>(
      { ...defaultIsIssuesOpen }
    );

    const setIsIssuesOpen = React.useCallback(
      (key: keyof IsIssuesOpenState, isOpen: boolean) => {
        setIsIssuesOpenFn((prev) => ({ ...prev, [key]: isOpen }));
      },
      []
    );

    const loadingKey = React.useRef('');

    const [isLoadingSummary, setIsLoadingSummary] = React.useState(false);

    const { evaluateQuestions } = useEvaluateQuestions();

    const evaluateSummary = React.useCallback(
      async (project?: Project): Promise<EvaluationSummary | null> => {
        if (!defaultConnection || !project || !diagram) {
          return null;
        }

        const currentLoadingKey = uuid();
        loadingKey.current = currentLoadingKey;

        setIsLoadingSummary(true);

        let evaluationSummary: EvaluationSummary | undefined;

        dispatch(uiSlice.actions.toggledVoiceboxDrawer({ isOpen: true }));

        try {
          const unevaluatedQuestions = questions.filter(
            (question) =>
              !question.evaluation ||
              !project.voiceboxEvaluatedCompetencyQuestions?.includes(
                question.id
              )
          );

          const loadedQuestions = await evaluateQuestions(
            unevaluatedQuestions,
            { skipFailureNotification: true }
          );

          if (loadingKey.current !== currentLoadingKey) {
            return null;
          }

          const competencyQuestions =
            loadedQuestions || diagram.model.modelData.competencyQuestions;

          const evaluationResponse = await requestStardogVoiceboxGenesis<
            GenerateSummaryRequest,
            GenerateSummaryResponse
          >({
            connectionIdx: defaultConnection.index,
            body: {
              endpoint: defaultConnection.endpoint,
              action: 'generate_summary',
              parameters: buildGenerateSummaryParameters(
                project,
                competencyQuestions
              ),
            },
          });

          if (loadingKey.current !== currentLoadingKey) {
            return null;
          }

          if (evaluationResponse.error) {
            throw new Error(evaluationResponse.error);
          }

          evaluationSummary = evaluationResponse?.results
            ? {
                summary: evaluationResponse.results.summary,
                issues: evaluationResponse.results.issues.map((issue) => {
                  const type =
                    issue.type.toUpperCase() as EvaluationSummaryIssueType;
                  let label = '';
                  if (type === EvaluationSummaryIssueType.UNANSWERED_QUESTION) {
                    label =
                      competencyQuestions.find((q) => q.id === issue.resource)
                        ?.question || 'Unknown question';
                  } else if (
                    type === EvaluationSummaryIssueType.UNLINKED_CONCEPT
                  ) {
                    label = getLocalName(issue.resource);
                  } else if (
                    type === EvaluationSummaryIssueType.UNUSED_DATASET
                  ) {
                    label =
                      diagram.model.nodeDataArray.find(
                        (data): data is Resource =>
                          isResource(data) && data.value === issue.resource
                      )?.text || 'Unknown dataset';
                  }
                  return {
                    type,
                    target: issue.resource,
                    label,
                    explanation: issue.explanation,
                  };
                }),
              }
            : undefined;

          if (evaluationSummary) {
            setIsIssuesOpenFn({ ...defaultIsIssuesOpen });
          }

          dispatch(
            projectsActionCreators.evaluatedProjectSummary(
              projectId,
              evaluationSummary
            )
          );
        } catch (err) {
          dispatch(notificationsActionCreators.voiceboxGenerateSummaryFailed());
        }

        setIsLoadingSummary(false);

        if (evaluationSummary) {
          dispatch(
            uiSlice.actions.toggledVoiceboxDrawerSummary({ isOpen: true })
          );
        }

        return evaluationSummary || null;
      },
      [
        defaultConnection,
        diagram,
        dispatch,
        evaluateQuestions,
        projectId,
        questions,
      ]
    );

    return {
      evaluateSummary,
      isLoadingSummary,
      isIssuesOpen,
      setIsIssuesOpen,
    };
  };
